import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  Paper,
  Typography,
  Container,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { useDispatch, useSelector } from "react-redux";
import {
  setMapOnFlyInfo,
  setIsAddCrossRefOpen,
  selectMapOnFlyInfo,
  selectToggle,
  setToggle,
} from "@/features/connections/crossReferenceSlice";
import AllowanceChargeCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/AllowanceChargeCatalogTab";
import CarrierCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/CarrierCatalogTab";
import CustomerCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/CustomerCatalogTab";
import ItemCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/ItemCatalogTab";
import ItemUOMCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/ItemUOMCatalogTab";
import ShipToCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/ShipToCatalogTab";
import WarehouseCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/WarehouseCatalogTab";
import TaxGroupCatalogTab from "@/components/Maintenance/MapOnFly/CatalogTabs/TaxGroupCatalogTab";
import {
  selectCrossReferenceSummarySelectedTab,
  selectSelectedConnectionPair,
} from "@/features/maintenance/maintenanceSlice";
import "./AddCrossReferencePopup.css";

const catalogTypeNames = [
  "Customer",
  "Item",
  "Item UOM",
  "Warehouse",
  "Ship To",
  "Carrier",
  "Allowance/Charge",
];

const tabs = [
  <CustomerCatalogTab />,
  <ItemCatalogTab />,
  <ItemUOMCatalogTab />,
  <WarehouseCatalogTab />,
  <ShipToCatalogTab />,
  <CarrierCatalogTab />,
  <AllowanceChargeCatalogTab />,
  <TaxGroupCatalogTab />
];

const AddCrossReferencePopup = () => {
  const dispatch = useDispatch();
  const catalogType = useSelector(selectCrossReferenceSummarySelectedTab);
  const connectionPair = useSelector(selectSelectedConnectionPair);
  const crossReferenceInfo =
    useSelector(selectMapOnFlyInfo)?.crossReferenceInfo;
  const [swapped, setSwapped] = useState(false);
  const toggle = useSelector(selectToggle);

  useEffect(() => {
    dispatch(setToggle({ value: false }));
  }, []);

  const handleToggle = () => {
    setSwapped((prev) => !prev);
    dispatch(setToggle({ value: !toggle })); // Need to detect changes to toggle in ItemUOM and ShipTo tabs
  };

  useEffect(() => {
    if (!connectionPair) return;
    const selectedCrossReferenceInfo = {
      ConnectionPairId: connectionPair.ConnectionPairId,
      SourceConnection: swapped
        ? connectionPair.ConnectionName2
        : connectionPair.ConnectionName1,
      SourceConnectionId: swapped
        ? connectionPair.ConnectionId2
        : connectionPair.ConnectionId1,
      TargetConnection: swapped
        ? connectionPair.ConnectionName1
        : connectionPair.ConnectionName2,
      TargetConnectionId: swapped
        ? connectionPair.ConnectionId1
        : connectionPair.ConnectionId2,
      IdentifiedCatalogs: null,
      CrossReferenceTypes: {
        Customer: 0,
        ShipTo: 4,
        Item: 1,
        UOM: 2,
        Warehouse: 3,
        Carrier: 5,
        MiscCharges: 6,
        TaxGroup: 7,
      },
    };
    dispatch(
      setMapOnFlyInfo({
        crossReferenceInfo: selectedCrossReferenceInfo,
        crossReferenceWorkflowRecordId: null,
      })
    );
  }, [connectionPair, swapped]);

  return (
    <div>
      <DialogTitle
        style={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
        }}
      >
        <div style={{ display: "flex" }}>
          <div style={{ fontWeight: "var(--font-semi-bold)" }}>
            {"Add Cross Reference - " + catalogTypeNames[catalogType]}
          </div>
        </div>
        <IconButton
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
          onClick={() => dispatch(setIsAddCrossRefOpen({ value: false }))}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className="dialogContent" style={{ overflow: "hidden" }}>
        <Box
          sx={{
            flex: "auto",
            marginLeft: "5px",
            width: "100%",
            backgroundColor: "var(--disabled)",
          }}
        >
          <Box className="connectionPair-details">
            <Paper elevation={0}>
              <Typography variant="h4">Connection Pair Details</Typography>
              <Typography variant="h6">
                {"Name: " +
                  crossReferenceInfo.SourceConnection +
                  " To " +
                  crossReferenceInfo.TargetConnection}
              </Typography>
            </Paper>
          </Box>
          <Box style={{ display: "flex", justifyContent: "flex-end" }}>
            <ShepButton variant="secondary" onClick={handleToggle}>
              Toggle Table
            </ShepButton>
          </Box>
          <Box>{tabs[catalogType]}</Box>
        </Box>
      </DialogContent>
    </div>
  );
};

export default AddCrossReferencePopup;
